import { createEffect, createMemo, createSignal, For, Show, type JSX } from 'solid-js'
import {
    formatAxisNumber,
    formatCalendarDay,
    formatClockTime,
    formatDuration,
    formatInstant,
    formatMetricValue
} from 'mormaii-web-presentation'
import type { MetricType } from 'mormaii-web-presentation'
import { SLEEP_STAGE_LABELS, SLEEP_STAGE_TOOLTIP } from 'mormaii-web-presentation/patient-analytics'

import { PAD, niceTicks } from './charts.axis'
import { useWidth } from './use-width'
import Css from './charts.module.scss'

export type ChartPoint = {
    time: number
    value: number
    high?: number
    low?: number
    /** O piso e o teto da sombra naquele instante - percentis 10 e 90 das pessoas (B-43). */
    min?: number
    max?: number
}

/**
 * Uma faixa de REFERENCIA no fundo do grafico (B-43): uma faixa de status da empresa, ou
 * uma zona de frequencia cardiaca por idade.
 *
 * `spans`, quando ha, limita a faixa a trechos do TEMPO - as zonas de treino so valem
 * enquanto a pessoa treinava. Sem `spans`, a faixa atravessa o grafico inteiro.
 */
export type ChartBand = {
    from: number
    to: number
    color: string
    label: string
    spans?: readonly { start: number; end: number }[]
}

type SeriesChartProps = {
    points: ChartPoint[]
    metric: MetricType
    height?: number
    /**
     * Desenha o HIPNOGRAMA: eixo de 0 a 3 rotulado Acordado/REM/Leve/Pesado.
     *
     * So o painel do PACIENTE pede isso. No painel da empresa o sono e uma curva de
     * total por dia, com o eixo em horas.
     */
    sleepStages?: boolean
    /**
     * Um intervalo que cabe num dia escreve a HORA no eixo; os maiores escrevem a
     * data, porque `08:00` de um intervalo de dez dias nao diz de qual dia e.
     */
    singleDay?: boolean
    /** As faixas de referencia do fundo (B-43) - so o grafico ampliado as passa. */
    bands?: readonly ChartBand[]
    /** Desenha a sombra entre `min` e `max` de cada ponto (B-43). */
    spread?: boolean
}

export function SeriesChart(props: SeriesChartProps): JSX.Element {
    const [width, observe] = useWidth()
    const [hover, setHover] = createSignal<number | undefined>()

    const height = () => props.height ?? 300
    const pressure = () => props.metric === 'PRESSAO'

    /**
     * O rotulo de um valor no EIXO Y.
     *
     * O eixo continua com a escrita propria (duracao por extenso no sono, uma casa nas
     * outras): as marcas do eixo estao na pauta do P.O. (relatos 1 e 2 de 07/10/2026).
     */
    const formatValue = (value: number) =>
        props.sleepStages
            ? (SLEEP_STAGE_LABELS[value] ?? String(value))
            : props.metric === 'SONO'
              ? formatDuration(value)
              : formatAxisNumber(value)

    /*
     * O sono fora do hipnograma vem um ponto por NOITE (desde 08/10/2026, como o cartao):
     * escreve-se o dia, e nao a hora `00:00` do ponto, que nao e hora de nada.
     */
    const daily = () => props.metric === 'SONO' && !props.sleepStages
    const formatTime = (time: number) =>
        daily() ? formatCalendarDay(time) : props.singleDay ? formatClockTime(time) : formatInstant(time)

    const bounds = createMemo(() => {
        const points = props.points
        if (points.length === 0) {
            return undefined
        }

        const times = points.map((p) => p.time)
        const values = pressure()
            ? points.flatMap((p) => [p.high ?? p.value, p.low ?? p.value])
            : props.spread
              ? points.flatMap((p) => [p.min ?? p.value, p.max ?? p.value])
              : points.map((p) => p.value)

        const minValue = props.sleepStages ? 0 : Math.min(...values)
        const maxValue = props.sleepStages ? 3 : Math.max(...values)
        // Uma folga de 8% em cima e embaixo: curva encostada na borda parece cortada.
        const slack = props.sleepStages ? 0 : (maxValue - minValue || 1) * 0.08

        return {
            minTime: Math.min(...times),
            maxTime: Math.max(...times),
            minValue: minValue - slack,
            maxValue: maxValue + slack
        }
    })

    const plot = () => ({
        width: Math.max(0, width() - PAD.left - PAD.right),
        height: Math.max(0, height() - PAD.top - PAD.bottom)
    })

    const scaleX = (time: number) => {
        const b = bounds()!
        const span = b.maxTime - b.minTime || 1
        return PAD.left + ((time - b.minTime) / span) * plot().width
    }

    const scaleY = (value: number) => {
        const b = bounds()!
        const span = b.maxValue - b.minValue || 1
        return PAD.top + plot().height - ((value - b.minValue) / span) * plot().height
    }

    /** O caminho da curva, e o mesmo caminho fechado embaixo para o preenchimento. */
    const paths = (pick: (p: ChartPoint) => number | undefined) => {
        const usable = props.points.filter((p) => pick(p) !== undefined)
        if (usable.length === 0) {
            return undefined
        }

        const line = usable
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(p.time).toFixed(2)} ${scaleY(pick(p)!).toFixed(2)}`)
            .join(' ')

        const floor = (PAD.top + plot().height).toFixed(2)
        const area = `${line} L ${scaleX(usable[usable.length - 1]!.time).toFixed(2)} ${floor} L ${scaleX(usable[0]!.time).toFixed(2)} ${floor} Z`

        return { line, area }
    }

    /** O contorno da sombra: pelo `max` da esquerda para a direita, e volta pelo `min`. */
    const spreadPath = () => {
        const usable = props.points.filter((p) => p.min !== undefined && p.max !== undefined)
        if (usable.length < 2) {
            return undefined
        }
        const top = usable.map(
            (p, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(p.time).toFixed(2)} ${scaleY(p.max!).toFixed(2)}`
        )
        const bottom = [...usable].reverse().map((p) => `L ${scaleX(p.time).toFixed(2)} ${scaleY(p.min!).toFixed(2)}`)
        return `${top.join(' ')} ${bottom.join(' ')} Z`
    }

    const yTicks = createMemo(() => {
        const b = bounds()
        if (!b) return []
        if (props.sleepStages) return [0, 1, 2, 3]

        /*
         * So as marcas DENTRO da escala. O `niceTicks` sempre cobre o maximo, e a marca
         * que passa dele (util ao `ComboChart`, que escala pelo topo do eixo) ficava aqui
         * ACIMA da area de desenho: com Frequencia Cardiaca de 01/09 a 30/09/2026, o
         * "120" saia do painel e aparecia entre os cartoes de baixo (B-33).
         */
        return niceTicks(b.minValue, b.maxValue).filter((tick) => tick >= b.minValue && tick <= b.maxValue)
    })

    const xTicks = createMemo(() => {
        const b = bounds()
        if (!b || props.points.length === 0) return []

        // Quatro marcas no maximo, e sempre pontos REAIS da serie - uma marca no
        // meio de um buraco de dois dias nomearia um instante que nao foi medido.
        const wanted = Math.min(4, props.points.length)
        const step = (props.points.length - 1) / Math.max(1, wanted - 1)
        return Array.from({ length: wanted }, (_, i) => props.points[Math.round(i * step)]!.time)
    })

    /** O ponto mais proximo do ponteiro, para o balao. */
    const onMove = (event: PointerEvent) => {
        const b = bounds()
        if (!b || props.points.length === 0) return

        const rect = (event.currentTarget as SVGElement).getBoundingClientRect()
        const x = event.clientX - rect.left
        let best = 0
        let bestDistance = Infinity
        props.points.forEach((point, index) => {
            const distance = Math.abs(scaleX(point.time) - x)
            if (distance < bestDistance) {
                bestDistance = distance
                best = index
            }
        })
        setHover(best)
    }

    let host!: HTMLDivElement
    createEffect(() => observe(host))

    return (
        <div ref={host} class={Css.frame} style={{ height: `${height()}px` }}>
            <Show when={bounds() && width() > 0} fallback={<p class={Css.empty}>Sem medição no período</p>}>
                <svg
                    width={width()}
                    height={height()}
                    class={Css.canvas}
                    onPointerMove={onMove}
                    onPointerLeave={() => setHover(undefined)}
                >
                    <defs>
                        <linearGradient id="series-fill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stop-color="var(--accent)" stop-opacity="0.22" />
                            <stop offset="100%" stop-color="var(--accent)" stop-opacity="0" />
                        </linearGradient>
                    </defs>

                    {/* A grade horizontal, e o rotulo de cada marca a esquerda. */}
                    <For each={yTicks()}>
                        {(tick) => (
                            <>
                                <line
                                    x1={PAD.left}
                                    x2={PAD.left + plot().width}
                                    y1={scaleY(tick)}
                                    y2={scaleY(tick)}
                                    stroke="var(--line-2)"
                                    stroke-dasharray="3 3"
                                />
                                <text
                                    x={PAD.left - 10}
                                    y={scaleY(tick)}
                                    text-anchor="end"
                                    dominant-baseline="middle"
                                    class={Css.axisNumber}
                                >
                                    {formatValue(tick)}
                                </text>
                            </>
                        )}
                    </For>

                    {/*
                        A primeira e a ultima marca caem na borda do grafico: centradas,
                        metade do texto ficava fora ("01/10/2026, 00:00" cortado a direita).
                    */}
                    <For each={xTicks()}>
                        {(time, index) => (
                            <text
                                x={scaleX(time)}
                                y={height() - 8}
                                text-anchor={
                                    xTicks().length > 1 && index() === 0
                                        ? 'start'
                                        : xTicks().length > 1 && index() === xTicks().length - 1
                                          ? 'end'
                                          : 'middle'
                                }
                                class={Css.axis}
                            >
                                {formatTime(time)}
                            </text>
                        )}
                    </For>

                    {/*
                        As REFERENCIAS (B-43), atras de tudo: as faixas de status ou as zonas
                        por idade, recortadas na area de desenho - uma faixa que vai alem dos
                        dados nao estica a escala, para a curva continuar legivel.
                    */}
                    <For each={props.bands ?? []}>
                        {(band) => {
                            const rect = (start: number, end: number) => {
                                const top = Math.max(PAD.top, scaleY(band.to))
                                const bottom = Math.min(PAD.top + plot().height, scaleY(band.from))
                                const left = Math.max(PAD.left, scaleX(start))
                                const right = Math.min(PAD.left + plot().width, scaleX(end))
                                return bottom > top && right > left
                                    ? { x: left, y: top, width: right - left, height: bottom - top }
                                    : undefined
                            }
                            const pieces = () =>
                                (band.spans ?? [{ start: bounds()!.minTime, end: bounds()!.maxTime }])
                                    .map((span) => rect(span.start, span.end))
                                    .filter((piece) => piece !== undefined)
                            return (
                                <For each={pieces()}>
                                    {(piece) => (
                                        <rect
                                            x={piece!.x}
                                            y={piece!.y}
                                            width={piece!.width}
                                            height={piece!.height}
                                            fill={band.color}
                                            fill-opacity="0.14"
                                        >
                                            <title>{band.label}</title>
                                        </rect>
                                    )}
                                </For>
                            )
                        }}
                    </For>

                    {/* A SOMBRA onde esta a maioria das pessoas: percentis 10 e 90 (B-43). */}
                    <Show when={props.spread && spreadPath()}>
                        {(path) => <path d={path()} fill="var(--accent)" fill-opacity="0.16" stroke="none" />}
                    </Show>

                    {/* A PRESSAO desenha DUAS curvas: sistolica e diastolica. */}
                    <Show when={pressure()} fallback={<Curve paths={paths((p) => p.value)} />}>
                        <Curve paths={paths((p) => p.high ?? p.value)} />
                        <Curve paths={paths((p) => p.low ?? p.value)} dashed />
                    </Show>

                    <Show when={hover() !== undefined && props.points[hover()!]}>
                        {(point) => (
                            <>
                                <line
                                    x1={scaleX(point().time)}
                                    x2={scaleX(point().time)}
                                    y1={PAD.top}
                                    y2={PAD.top + plot().height}
                                    stroke="var(--accent)"
                                    stroke-width="1"
                                />
                                <circle
                                    cx={scaleX(point().time)}
                                    cy={scaleY(pressure() ? (point().high ?? point().value) : point().value)}
                                    r="4"
                                    fill="var(--accent)"
                                    stroke="white"
                                    stroke-width="2"
                                />
                            </>
                        )}
                    </Show>
                </svg>

                {/*
                    O balao e HTML, e nao `<text>` dentro do SVG: ele tem fundo,
                    borda, duas linhas e precisa medir o proprio texto - tudo o que
                    o SVG faz mal e o CSS faz de graca.
                */}
                <Show when={hover() !== undefined && props.points[hover()!]}>
                    {(point) => (
                        <div
                            class={Css.tooltip}
                            style={{
                                left: `${Math.min(Math.max(scaleX(point().time), 60), width() - 60)}px`,
                                top: `${scaleY(pressure() ? (point().high ?? point().value) : point().value) - 10}px`
                            }}
                        >
                            <p class={Css.tooltipTime}>{formatTime(point().time)}</p>
                            <Show
                                when={pressure()}
                                fallback={
                                    <p class={Css.tooltipValue}>
                                        {/* O balao escreve como o CARTAO escreve - a mesma funcao. */}
                                        {props.sleepStages
                                            ? (SLEEP_STAGE_TOOLTIP[point().value] ?? String(point().value))
                                            : formatMetricValue(props.metric, point().value)}
                                    </p>
                                }
                            >
                                <p class={Css.tooltipValue}>
                                    {Math.round(point().high ?? point().value)}/
                                    {Math.round(point().low ?? point().value)}
                                </p>
                            </Show>
                        </div>
                    )}
                </Show>
            </Show>
        </div>
    )
}

function Curve(props: { paths?: { line: string; area: string }; dashed?: boolean }): JSX.Element {
    return (
        <Show when={props.paths}>
            {(paths) => (
                <>
                    <path d={paths().area} fill="url(#series-fill)" />
                    <path
                        d={paths().line}
                        fill="none"
                        stroke="var(--accent)"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-dasharray={props.dashed ? '5 4' : undefined}
                    />
                </>
            )}
        </Show>
    )
}
